import React from 'react'
import { Box, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon
} from '@mui/icons-material'
import {
  createAction,
  createColumns
} from '../../../components/_EOS/GenericTable'

export interface ActiveDay {
  dayId: number
  name: string
}

export interface CourierServiceGridDto {
  courierServiceId: number
  name: string
  courierName: string
  maxUnitsCapacity: number | null
  maxPackagesCapacity: number | null
  active: boolean
  activeDays: ActiveDay[]
}

export interface PickupTimeSaveDto {
  pickupTime: string // formato HH:mm
  cutOffMinutes: number
}

export interface WeeklyScheduleSaveDto {
  dayId: number
  active: boolean
  pickupTimes: PickupTimeSaveDto[]
}

export interface ExceptionScheduleSaveDto {
  description: string
  exceptionDate: string // formato yyyy-MM-dd
  active: boolean
  pickupTimes: PickupTimeSaveDto[]
}

export interface CourierServiceScheduleSaveDto {
  courierServiceId: number
  maxUnitsCapacity: number | null
  maxPackagesCapacity: number | null
  weeklySchedules: WeeklyScheduleSaveDto[]
  exceptions: ExceptionScheduleSaveDto[]
}

export interface PickupTimeDto {
  courierServicePickupScheduleId: number
  pickupTime: string // formato HH:mm:ss
  cutOffMinutes: number
}

export interface WeeklyScheduleDto {
  courierServiceScheduleId: number
  dayId: number
  dayName: string
  active: boolean
  pickupTimes: PickupTimeDto[]
}

export interface ExceptionScheduleDto {
  courierServiceScheduleId: number
  description: string
  exceptionDate: string | null
  active: boolean
  pickupTimes: PickupTimeDto[]
}

export interface CourierServiceScheduleDetailDto {
  courierServiceId: number
  weeklySchedules: WeeklyScheduleDto[]
  exceptions: ExceptionScheduleDto[]
}

// CourierCollectionPlannerFormEdit: límites válidos para el campo "Tiempo de Corte" (minutos)
export const CUT_OFF_MINUTES_MIN = 1
export const CUT_OFF_MINUTES_MAX = 1440

// CourierCollectionPlannerFormEdit: valida que el tiempo de corte esté dentro del rango permitido
export const isCutOffMinutesValid = (minutes: number) =>
  minutes >= CUT_OFF_MINUTES_MIN && minutes <= CUT_OFF_MINUTES_MAX

// CourierCollectionPlannerFormEdit: valida que la capacidad máxima de unidades sea mayor a 0
export const isMaxUnitsCapacityValid = (value: string) => value === '' || Number(value) > 0

// CourierCollectionPlannerFormEdit: valida que la capacidad máxima de paquetes sea mayor a 0
export const isMaxPackagesCapacityValid = (value: string) => value === '' || Number(value) > 0

// CourierCollectionPlannerFormEdit: calcula la hora límite de aceptación de pedidos (hora de recolección - tiempo de corte)
export const calculateCutOffTime = (pickupTime: string, cutOffMinutes: number): string | null => {
  if (!pickupTime || !isCutOffMinutesValid(cutOffMinutes)) return null

  const [hours, minutes] = pickupTime.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

  const totalMinutes = ((hours * 60 + minutes - cutOffMinutes) % CUT_OFF_MINUTES_MAX + CUT_OFF_MINUTES_MAX) % CUT_OFF_MINUTES_MAX
  const cutOffHours = Math.floor(totalMinutes / 60)
  const cutOffMins = totalMinutes % 60

  return `${String(cutOffHours).padStart(2, '0')}:${String(cutOffMins).padStart(2, '0')}`
}

// CourierCollectionPlannerFormEdit: indica si la hora límite calculada cae en el día anterior a la recolección
export const isCutOffPreviousDay = (pickupTime: string, cutOffMinutes: number): boolean => {
  if (!pickupTime || !isCutOffMinutesValid(cutOffMinutes)) return false

  const [hours, minutes] = pickupTime.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false

  return hours * 60 + minutes - cutOffMinutes < 0
}

// CourierCollectionPlannerFormDetail: props del formulario de solo lectura para ver los horarios de un courier service
export interface CourierCollectionPlannerFormDetailProps {
  courier: CourierServiceGridDto
}

// CourierCollectionPlannerFormEdit: ref expuesta al modal para disparar el guardado desde el botón "Guardar cambios"
export interface CourierCollectionPlannerFormEditRef {
  save: () => void
}

// CourierCollectionPlannerFormEdit: props del formulario de edición de horarios de un courier service
export interface CourierCollectionPlannerFormEditProps {
  courier: CourierServiceGridDto
  onSave: (success?: boolean) => void
  onSavingChange?: (loading: boolean) => void
  handleSnackbar?: (msg: string | undefined, status: boolean) => void
}

// CourierCollectionPlannerFormEdit: fila usada por la grilla de horarios de recolección (GenericTableV2)
export interface PickupTimeRow extends PickupTimeSaveDto {
  id: number
}

// CourierCollectionPlannerFormEdit: columnas de la grilla de horarios de recolección (Horarios Semanales y Excepciones)
export const createPickupTimeColumns = (
  onChangeTime: (index: number, field: keyof PickupTimeSaveDto, value: string) => void
) =>
  createColumns<PickupTimeRow>([
    {
      key: 'id',
      title: 'Recolección',
      align: 'center',
      headerTooltip: 'Número correlativo de la recolección configurada',
      render: (value, row) => {
        const hasExtraLine = !!calculateCutOffTime(row.pickupTime, row.cutOffMinutes) || !isCutOffMinutesValid(row.cutOffMinutes)
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
            {`#${(value as number) + 1}`}
            {hasExtraLine && (
              <Typography variant='caption' sx={{ fontSize: '0.75rem', lineHeight: 1, margin: 0, mt: '3px', visibility: 'hidden' }}>
                &nbsp;
              </Typography>
            )}
          </Box>
        )
      }
    },
    {
      key: 'pickupTime',
      title: 'Hora de Recolección',
      headerTooltip: 'Hora especifica para el retiro de paquetes.',
      render: (value, row) => {
        const hasExtraLine = !!calculateCutOffTime(row.pickupTime, row.cutOffMinutes) || !isCutOffMinutesValid(row.cutOffMinutes)
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
            <TextField
              type='time'
              size='small'
              variant='outlined'
              value={value as string}
              onChange={(e) => onChangeTime(row.id, 'pickupTime', e.target.value)}
            />
            {hasExtraLine && (
              <Typography variant='caption' sx={{ fontSize: '0.75rem', lineHeight: 1, margin: 0, mt: '3px', visibility: 'hidden' }}>
                &nbsp;
              </Typography>
            )}
          </Box>
        )
      }
    },
    {
      key: 'cutOffMinutes',
      title: 'Tiempo de Corte',
      headerTooltip: 'Tiempo antes de la recolección en los que se cierra ese envio de pedidos.',
      render: (value, row) => {
        const cutOffMinutes = value as number
        const isInvalid = !isCutOffMinutesValid(cutOffMinutes)
        const cutOffTime = calculateCutOffTime(row.pickupTime, cutOffMinutes)
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                type='number'
                size='small'
                variant='outlined'
                sx={{ width: 160 }}
                value={cutOffMinutes}
                error={isInvalid}
                helperText={isInvalid ? `Entre ${CUT_OFF_MINUTES_MIN} y ${CUT_OFF_MINUTES_MAX}` : undefined}
                slotProps={{ htmlInput: { maxLength: 4, min: CUT_OFF_MINUTES_MIN, max: CUT_OFF_MINUTES_MAX } }}
                onChange={(e) => onChangeTime(row.id, 'cutOffMinutes', e.target.value.slice(0, 4))}
              />
              <Typography variant='body2' color='textSecondary'>
                Minutos
              </Typography>
            </Box>
            {cutOffTime && (
              <Typography variant='caption' color='secondary' sx={{ fontSize: '0.75rem', lineHeight: 1, margin: 0, mt: '3px' }}>
                {`Se aceptan pedidos hasta las ${cutOffTime}${isCutOffPreviousDay(row.pickupTime, cutOffMinutes) ? ' del día anterior' : ''}`}
              </Typography>
            )}
          </Box>
        )
      }
    }
  ])

// CourierCollectionPlannerFormEdit: acciones de la grilla de horarios de recolección (Horarios Semanales y Excepciones)
export const createPickupTimeActions = (onRemove: (index: number) => void, isSaving: boolean) => [
  createAction<PickupTimeRow>({
    key: 'delete',
    label: 'Eliminar',
    icon: <DeleteIcon fontSize='small' />,
    onClick: (row) => onRemove(row.id),
    disabled: () => isSaving,
    render: (row) => {
      const hasExtraLine = !!calculateCutOffTime(row.pickupTime, row.cutOffMinutes) || !isCutOffMinutesValid(row.cutOffMinutes)
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
          <Tooltip title='Eliminar' arrow>
            <span>
              <IconButton
                size='small'
                onClick={() => onRemove(row.id)}
                disabled={isSaving}
                color='default'
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          {hasExtraLine && (
            <Typography variant='caption' sx={{ fontSize: '0.75rem', lineHeight: 1, margin: 0, mt: '3px', visibility: 'hidden' }}>
              &nbsp;
            </Typography>
          )}
        </Box>
      )
    }
  })
]

export const WEEK_DAYS: { dayId: number; name: string }[] = [
  { dayId: 1, name: 'Lunes' },
  { dayId: 2, name: 'Martes' },
  { dayId: 3, name: 'Miércoles' },
  { dayId: 4, name: 'Jueves' },
  { dayId: 5, name: 'Viernes' },
  { dayId: 6, name: 'Sábado' },
  { dayId: 7, name: 'Domingo' }
]

export const tableColumns = createColumns<CourierServiceGridDto>([
  { key: 'name', title: 'Servicio' },
  { key: 'courierName', title: 'Courier' },
  { key: 'maxUnitsCapacity', title: 'Cap. Unidades' },
  { key: 'maxPackagesCapacity', title: 'Cap. Paquetes' },
  {
    key: 'activeDays',
    title: 'Días Activos',
    render: (value) => (
      <Box display='flex' flexWrap='wrap' gap={0.5} justifyContent='center'>
        {(value as unknown as ActiveDay[])?.map((day) => (
          <Chip key={day.dayId} label={day.name} size='small' />
        ))}
      </Box>
    )
  }
])

// CourierCollectionPlannerPage: acciones de la grilla principal de couriers (ver/editar/eliminar)
export const createCourierActions = (
  onView: (row: CourierServiceGridDto) => void,
  onEdit: (row: CourierServiceGridDto) => void,
  onDelete: (row: CourierServiceGridDto) => void
) => [
  createAction<CourierServiceGridDto>({
    key: 'edit',
    label: 'Editar',
    icon: <EditIcon fontSize='small' />,
    onClick: onEdit,
    color: 'default'
  }),
  createAction<CourierServiceGridDto>({
    key: 'view',
    label: 'Ver Detalle',
    icon: <ViewIcon fontSize='small' />,
    onClick: onView,
    color: 'default',
    disabled: (row) => !row.activeDays || row.activeDays.length === 0
  }),
  createAction<CourierServiceGridDto>({
    key: 'delete',
    label: 'Eliminar',
    icon: <DeleteIcon fontSize='small' />,
    onClick: onDelete,
    color: 'default',
    disabled: (row) => !row.activeDays || row.activeDays.length === 0
  })
]
